import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, TrendingUp, Settings, Users, CheckCircle, ArrowUpRight, Quote } from "lucide-react";

const HomePage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const pillars = [
    {
      icon: TrendingUp,
      title: "Cashflow-sturing",
      description: "Verbetering van liquiditeit door een snellere doorstroom van omzet naar cash.",
    },
    {
      icon: Settings,
      title: "Structuur & Proces",
      description: "Ontwerp van heldere processen en duidelijke afspraken tussen teams.",
    },
    {
      icon: Users,
      title: "Leiderschap & Eigenaarschap",
      description: "Teams die verantwoordelijkheid nemen voor resultaten en prestaties.",
    },
  ];

  const benefits = [
    "Voorspelbare cashflow in plaats van financiële onrust",
    "Snellere doorlooptijd van intake tot betaling",
    "Duidelijk eigenaarschap in elke processtap",
    "Betere samenwerking tussen afdelingen",
    "Meer tijd voor strategisch werk"
  ];

  const targetAudience = [
    "Advocatenkantoren",
    "Consultancybureaus",
    "Agencies",
    "Kennisorganisaties",
    "Zakelijke dienstverleners"
  ];

  const testimonialPreviews = [
    {
      quote: "Rabia heeft bij Van Doorne een wezenlijk verschil gemaakt in ons service-to-cash proces. Ze bracht op een doortastende en methodische manier overzicht, orde en rust — zowel in de processen als in het team.",
      name: "Anouk Rutten",
      title: "Board Member | COO",
      company: "Van Doorne"
    },
    {
      quote: "Rabia is voor mij bepalend geweest in de ontwerp-, bouw- en implementatiefase van een nieuw financieel systeem voor debiteurenbeheer. Ketendenken is haar kracht.",
      name: "Edwin Oversteeg",
      title: "Business Change Manager Service Delivery",
      company: "Achmea"
    }
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section - Light (#f5f4f2) */}
      <section className="relative lg:min-h-screen flex items-center pt-20 lg:pt-24 pb-8 lg:pb-12 bg-mist">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1761437855598-011cf89b2ad4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdvbGQlMjB3YXZlJTIwbHV4dXJ5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzI5ODA1MjR8MA&ixlib=rb-4.1.0&q=85"
            alt="Abstract gold wave"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-mist via-mist/95 to-mist/80" />
        </div>

        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-0 items-center">
            {/* Hero Visual - Shows first on mobile, right side on desktop */}
            <motion.div 
              className="order-1 lg:order-2 relative lg:col-span-7"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative lg:pl-8">
                {/* Image with proper cropping - shows face, posture and hands */}
                <img
                  src="/rabia-hero-optimized.jpg"
                  alt="Rabia Boutkabout - Financiële Interventie Strateeg"
                  className="w-full aspect-[5/4] sm:aspect-[16/10] lg:aspect-[4/5] object-cover object-[center_25%] lg:object-top lg:max-w-lg lg:ml-auto shadow-xl lg:shadow-2xl"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>

            {/* Text Content - Shows second on mobile, left side on desktop */}
            <div className="order-2 lg:order-1 lg:col-span-5 lg:pr-8">
              <motion.h1 
                {...fadeUp}
                transition={{ delay: 0.1, duration: 0.6 }}
                className="text-[1.6rem] sm:text-[2rem] lg:text-[2.85rem] xl:text-[3.25rem] font-serif font-medium tracking-tight leading-[1.15] text-burgundy mb-3 lg:mb-6"
                data-testid="hero-headline"
              >
                Je omzet groeit.<br /><span className="text-navy">Maar je cash blijft achter.</span>
              </motion.h1>
              
              <motion.div 
                {...fadeUp}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-[0.95rem] lg:text-lg leading-relaxed font-light text-navy max-w-xl mb-5 lg:mb-8 space-y-3"
                data-testid="hero-subheadline"
              >
                <p>Onrust in je cijfers.<br />Geen overzicht in je operatie.</p>
                <p>Je voelt dat omzet blijft hangen maar niemand stuurt het geheel.</p>
                <p className="text-berry font-medium leading-snug">Wij brengen met één duidelijke aanpak rust, structuur en regie.<br />Zodat cashflow weer voorspelbaar wordt.</p>
              </motion.div>

              <motion.div 
                {...fadeUp}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-3 lg:gap-4"
              >
                <a
                  href="https://calendly.com/rabia-regieopcashflow/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-primary"
                  className="bg-cta text-white px-6 lg:px-8 py-3.5 lg:py-4 rounded-sm hover:bg-cta-dark transition-all duration-300 text-sm tracking-widest uppercase font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 inline-flex items-center justify-center gap-2"
                >
                  Plan nu een gesprek
                  <ArrowRight size={18} />
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview Section - Light (#f5f4f2) */}
      <section className="section-padding bg-mist" data-testid="testimonials-preview-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.span {...fadeUp} className="text-sm tracking-widest uppercase font-semibold text-berry mb-4 block">
              Referenties
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-burgundy">
              Wat klanten zeggen over de samenwerking
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonialPreviews.map((testimonial, idx) => (
              <motion.div
                key={testimonial.name}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="bg-white p-8 md:p-10 relative border border-sand"
                data-testid={`testimonial-preview-${idx}`}
              >
                <Quote className="w-10 h-10 text-sand absolute top-6 right-6" />
                <p className="text-navy leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-sand pt-4">
                  <span className="font-semibold text-burgundy block">{testimonial.name}</span>
                  <span className="text-sm text-navy/70">{testimonial.title}</span>
                  <span className="text-sm text-berry block">{testimonial.company}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} transition={{ delay: 0.3 }} className="text-center mt-12">
            <Link
              to="/referenties"
              data-testid="view-all-testimonials"
              className="border border-burgundy text-burgundy px-8 py-4 rounded-sm hover:bg-burgundy hover:text-white transition-all duration-300 text-sm tracking-widest uppercase font-semibold inline-flex items-center gap-2"
            >
              Bekijk alle referenties
              <ArrowRight size={18} />
            </Link>
          </motion.div>

          {/* Client Logos */}
          <motion.div 
            {...fadeUp} 
            transition={{ delay: 0.4 }} 
            className="mt-16 pt-12 border-t border-sand"
            data-testid="client-logos"
          >
            <p className="text-center text-navy/60 text-sm mb-8">
              Samengewerkt met o.a.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16">
              <img
                src="https://customer-assets.emergentagent.com/job_fba38989-5870-42b4-b549-5a4d1f870233/artifacts/vkpn2lfn_Logo%20Achmea.jpg"
                alt="Achmea"
                className="h-10 md:h-12 object-contain grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                data-testid="logo-achmea"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_fba38989-5870-42b4-b549-5a4d1f870233/artifacts/kfhl3lml_Logo%20van%20Doorne.jpg"
                alt="Van Doorne"
                className="h-10 md:h-12 object-contain grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                data-testid="logo-van-doorne"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement Section - Sand (#d5c9bf) */}
      <section className="section-padding bg-sand">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.span {...fadeUp} className="text-sm tracking-widest uppercase font-semibold text-berry mb-4 block">
              Het Probleem
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-burgundy mb-6">
              Omzet op papier, stress in de praktijk
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-lg md:text-xl leading-relaxed font-light text-navy">
              Je organisatie draait goed. De opdrachten komen binnen, projecten lopen. 
              Maar toch blijft dat gevoel dat er iets niet klopt: waarom komt het geld 
              niet sneller binnen?
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { label: "Te late facturatie", desc: "Werk is afgerond maar de factuur volgt weken later." },
              { label: "Onduidelijke verantwoordelijkheid", desc: "Niemand pakt eigenaarschap over de doorstroom." },
              { label: "Versnipperde samenwerking", desc: "Afdelingen werken langs elkaar heen." },
              { label: "Financiële sturing ontbreekt", desc: "Er wordt vooral achteraf bijgestuurd." },
            ].map((item, idx) => (
              <motion.div
                key={item.label}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="flex items-start gap-4 p-6 bg-mist rounded-sm"
                data-testid={`problem-item-${idx}`}
              >
                <div className="w-1 h-full bg-berry rounded-full" />
                <div>
                  <h4 className="font-semibold text-burgundy mb-1">{item.label}</h4>
                  <p className="text-navy/70 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars Section - Light (#f5f4f2) */}
      <section className="section-padding bg-mist" data-testid="pillars-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.span {...fadeUp} className="text-sm tracking-widest uppercase font-semibold text-berry mb-4 block">
              De Aanpak
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-burgundy mb-6">
              De aanpak achter voorspelbare cashflow
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-lg md:text-xl leading-relaxed font-light text-navy max-w-2xl mx-auto">
              Geen losse verbeteringen. Maar een samenhangende aanpak die zorgt dat je organisatie daadwerkelijk doorstroomt naar cash.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                {...fadeUp}
                transition={{ delay: 0.1 * idx }}
                className="bg-white p-8 md:p-12 border border-sand hover:border-berry/30 transition-all duration-500 relative overflow-hidden group"
                data-testid={`pillar-card-${idx}`}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-berry transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                <pillar.icon className="w-10 h-10 text-berry mb-6" strokeWidth={1} />
                <h3 className="text-2xl md:text-3xl font-serif font-normal text-burgundy mb-4">{pillar.title}</h3>
                <p className="text-base leading-relaxed text-navy">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section - Burgundy Accent (#4f0e30) */}
      <section className="section-padding bg-burgundy text-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.span {...fadeUp} className="text-cta text-xs tracking-widest uppercase mb-4 block">
                Het Resultaat
              </motion.span>
              <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-white mb-6">
                Wat dit oplevert
              </motion.h2>
              <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-white/70 text-lg leading-relaxed mb-8">
                Wanneer de aanpak klopt, verandert niet alleen je cashflow maar je hele organisatie.
              </motion.p>
              <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
                <Link
                  to="/diensten"
                  data-testid="results-cta"
                  className="inline-flex items-center gap-2 text-cta hover:text-white transition-colors duration-300 font-medium"
                >
                  Bekijk de aanpak
                  <ArrowUpRight size={18} />
                </Link>
              </motion.div>
            </div>

            <div className="space-y-4">
              {benefits.map((benefit, idx) => (
                <motion.div
                  key={benefit}
                  {...fadeUp}
                  transition={{ delay: 0.1 * idx }}
                  className="flex items-center gap-4 p-4 bg-white/5 rounded-sm"
                  data-testid={`benefit-item-${idx}`}
                >
                  <CheckCircle className="w-5 h-5 text-cta flex-shrink-0" />
                  <span className="text-white/90">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section - Light (#f5f4f2) */}
      <section className="section-padding bg-mist">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <img
                src="https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZ2xhc3MlMjBtZWV0aW5nJTIwcm9vbSUyMGx1eHVyeXxlbnwwfHx8fDE3NzI5ODA1Mjl8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern meeting room"
                className="w-full aspect-[4/3] object-cover shadow-lg"
                data-testid="audience-image"
              />
            </motion.div>

            <div>
              <motion.span {...fadeUp} className="text-sm tracking-widest uppercase font-semibold text-berry mb-4 block">
                Voor Wie
              </motion.span>
              <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-burgundy mb-6">
                Professionele dienstverleners
              </motion.h2>
              <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-lg md:text-xl leading-relaxed font-light text-navy mb-8">
                Regie op Cashflow werkt met organisaties waar de dienstverlening 
                centraal staat maar complexe operationele processen de cashflow vertragen.
              </motion.p>

              <div className="grid grid-cols-2 gap-4">
                {targetAudience.map((audience, idx) => (
                  <motion.div
                    key={audience}
                    {...fadeUp}
                    transition={{ delay: 0.1 * idx }}
                    className="flex items-center gap-3"
                    data-testid={`audience-item-${idx}`}
                  >
                    <div className="w-2 h-2 bg-berry rounded-full" />
                    <span className="text-navy">{audience}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Sand (#d5c9bf) */}
      <section className="section-padding bg-sand relative overflow-hidden">
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span {...fadeUp} className="text-sm tracking-widest uppercase font-semibold text-berry mb-4 block">
              Volgende Stap
            </motion.span>
            <motion.h2 {...fadeUp} transition={{ delay: 0.1 }} className="text-3xl md:text-4xl lg:text-5xl font-serif font-normal tracking-tight text-burgundy mb-6">
              Klaar om jouw cashflow te verbeteren?
            </motion.h2>
            <motion.p {...fadeUp} transition={{ delay: 0.2 }} className="text-lg md:text-xl leading-relaxed font-light text-navy mb-4">
              In één gesprek zie je waar jouw cash vastloopt en welke stap nu het meeste impact maakt.
            </motion.p>
            <motion.p {...fadeUp} transition={{ delay: 0.25 }} className="text-navy/70 mb-10">
              Directe helderheid.
            </motion.p>
            <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
              <a
                href="https://calendly.com/rabia-regieopcashflow/30min"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="final-cta"
                className="bg-cta text-white px-8 py-4 rounded-sm hover:bg-cta-dark transition-all duration-300 text-sm tracking-widest uppercase font-semibold shadow-lg hover:shadow-xl hover:-translate-y-1 inline-flex items-center justify-center gap-2"
              >
                Plan een gesprek
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
